import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star } from "lucide-react";

export const WhyAcos = () => {
  return (
    <section className="relative py-24 overflow-hidden">
      {/* Complex gradient background */}
      <div className="absolute inset-0">
        {/* Base gradient transition from purple to black */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#2D1949] via-[#1a1333] to-black" />
        
        {/* Radial purple glow that fades as you scroll */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(147,51,234,0.15)_0%,transparent_50%)]" />
        
        {/* Multiple layered purple glows for depth */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(147,51,234,0.1)_0%,transparent_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_60%_30%,rgba(168,85,247,0.08)_0%,transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_40%_70%,rgba(126,34,206,0.08)_0%,transparent_40%)]" />
        </div>
        
        {/* Grid pattern that fades out */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(147,51,234,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(147,51,234,0.05)_1px,transparent_1px)] bg-[size:14px_24px] opacity-[0.15]" />
        
        {/* Enhanced bottom fade to black */}
        <div className="absolute inset-x-0 bottom-0 h-[70vh] bg-gradient-to-b from-transparent via-black/70 to-black" />
      </div>

      {/* Original grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:14px_24px] opacity-50" />

      {/* Decorative Stars */}
      <div className="absolute inset-0 z-10">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={`star-${i}`}
            className="absolute text-white text-2xl"
            initial={{ opacity: 0.2, rotate: 0 }}
            animate={{
              opacity: [0.2, 0.8, 0.2],
              rotate: 360,
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 5 + Math.random() * 3,
              repeat: Infinity,
              delay: i * 0.2,
              ease: "easeInOut",
            }}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
          >
            ✧
          </motion.div>
        ))}
      </div>
      
      {/* Content Container */}
      <div className="container mx-auto px-4 relative">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-24"
          >
            <motion.h2 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-4xl md:text-6xl font-bold mb-6 leading-tight max-w-4xl"
            >
              <span className="text-white">AI that thinks, learns,</span>
              <br />
              <motion.span
                animate={{ 
                  backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] 
                }}
                transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                className="bg-gradient-to-r from-purple-400 via-purple-500 to-purple-400 bg-clip-text text-transparent bg-[length:300%_auto]"
              >
                and closes deals autonomously
              </motion.span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-xl md:text-2xl text-gray-400 max-w-3xl mb-8"
            >
              No more manual prospecting, endless tool switching, or generic outreach.{' '}
              <span className="bg-gradient-to-r from-purple-400 via-purple-500 to-purple-400 bg-clip-text text-transparent">
                Just pure, Agentic sales excellence.
              </span>
            </motion.p>
          </motion.div>

          {/* Metrics with refined gradient borders */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 relative"
          >
            {[
              { value: "85%", label: "Time Saved on Prospecting" },
              { value: "10x", label: "More Qualified Leads" },
              { value: "3.5x", label: "Higher Response Rates" }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="relative group"
              >
                <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-purple-500/10 via-purple-600/20 to-purple-500/10 p-[1px]">
                  <div className="relative bg-black/40 backdrop-blur-sm rounded-xl p-8">
                    <motion.div
                      animate={{ scale: [1, 1.02, 1] }}
                      transition={{ duration: 3, repeat: Infinity }}
                      className="text-4xl font-bold text-white mb-3"
                    >
                      {stat.value}
                    </motion.div>
                    <div className="text-gray-400">{stat.label}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhyAcos;
